/**
 * Onboarding Buttons
 *
 * Action buttons for the Onboarding compound component.
 * NextButton (primary), BackButton (secondary), SkipButton (ghost).
 */

import * as React from 'react';
import { Button } from '@newcore/ui/buttons';
import { cn } from '@newcore/ui/cn';
import { Sizes } from '@newcore/ui/enums/sizes';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import type {
  OnboardingBackButtonProps,
  OnboardingNextButtonProps,
  OnboardingSkipButtonProps,
} from './types';

// =============================================================================
// Next Button (Primary Action)
// =============================================================================

/**
 * Primary action button with loading state
 *
 * @example
 * <Onboarding.NextButton onClick={handleNext} loading={isLoading}>
 *   Continue
 * </Onboarding.NextButton>
 */
export const OnboardingNextButton: React.FC<OnboardingNextButtonProps> = ({
  children = 'Continue',
  loading = false,
  disabled = false,
  onClick,
  type = 'button',
  className,
}): React.ReactElement => {
  return (
    <Button
      type={type}
      size={Sizes.LG}
      onClick={onClick}
      disabled={disabled}
      isLoading={loading}
      className={cn('min-w-[120px]', className)}
    >
      {children}
    </Button>
  );
};

// =============================================================================
// Back Button (Secondary Action)
// =============================================================================

/**
 * Secondary/cancel action button
 *
 * @example
 * <Onboarding.BackButton onClick={handleBack}>
 *   Cancel
 * </Onboarding.BackButton>
 */
export const OnboardingBackButton: React.FC<OnboardingBackButtonProps> = ({
  children = 'Back',
  disabled = false,
  onClick,
  className,
}): React.ReactElement => {
  return (
    <Button
      type='button'
      variant={ButtonVariants.SECONDARY}
      size={Sizes.LG}
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      {children}
    </Button>
  );
};

// =============================================================================
// Skip Button (Ghost Action)
// =============================================================================

/**
 * Ghost button for optional/skip actions
 *
 * @example
 * <Onboarding.SkipButton onClick={handleSkip}>
 *   Skip for now
 * </Onboarding.SkipButton>
 */
export const OnboardingSkipButton: React.FC<OnboardingSkipButtonProps> = ({
  children = 'Skip',
  onClick,
  className,
}): React.ReactElement => {
  return (
    <Button
      type='button'
      variant={ButtonVariants.TERTIARY}
      size={Sizes.LG}
      onClick={onClick}
      className={className}
    >
      {children}
    </Button>
  );
};
